import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/shallow";
import { useCreateBankAccount } from "../../../../hooks/queries/useMerchantQueries";
import {
  useVerifyBankAccount,
  // FUTURE RELEASE: MSISDN verification hook — re-enable when phone number bank verification is supported
  // useVerifyBankAccountViaMsisdn,
} from "../../../../hooks/queries/useBanksQueries";
import useModalManager from "./useModalManager";
import useBankAccountStore from "../../../../stores/bankAccountStore";
import createAccountSubmitHandler from "../utils/accountSubmitHandler";

const useBankAccountHandlers = () => {
  const { modalState, openModal, closeModal } = useModalManager();

  const { mutate: createBankAccount, isPending: isSaving } =
    useCreateBankAccount();
  const { mutate: verifyBankAccount, isPending: isVerifyingBankAccount } =
    useVerifyBankAccount();
  // FUTURE RELEASE: MSISDN mutation — re-enable when phone number bank verification is supported
  // const { mutate: verifyViaMsisdn, isPending: isVerifyingMsisdn } =
  //   useVerifyBankAccountViaMsisdn();

  const isVerifying = isVerifyingBankAccount; // FUTURE RELEASE: restore `|| isVerifyingMsisdn` when MSISDN is re-enabled

  const {
    currentStep,
    step1Draft,
    verifiedData,
    startFlow,
    setStep,
    setStep1Draft,
    setVerifiedData,
    resetFlow,
  } = useBankAccountStore(
    useShallow((state) => ({
      currentStep: state.currentStep,
      step1Draft: state.step1Draft,
      verifiedData: state.verifiedData,
      startFlow: state.startFlow,
      setStep: state.setStep,
      setStep1Draft: state.setStep1Draft,
      setVerifiedData: state.setVerifiedData,
      resetFlow: state.resetFlow,
    })),
  );

  useEffect(() => {
    const { isFlowActive: wasActive, resetFlow: reset } =
      useBankAccountStore.getState();
    if (wasActive) reset();
  }, []);

  const handleOpenModal = useCallback(() => {
    resetFlow();
    startFlow();
    openModal("bank");
  }, [resetFlow, startFlow, openModal]);

  const handleCloseModal = useCallback(() => {
    resetFlow();
    closeModal("bank");
  }, [resetFlow, closeModal]);

  const handleGoToStep2 = useCallback((verificationResponse) => {
    setVerifiedData(verificationResponse);
    setStep(2);
  }, [setVerifiedData, setStep]);

  const handleGoBackToStep1 = useCallback(() => {
    setStep(1);
  }, [setStep]);

  const handleSave = createAccountSubmitHandler(
    createBankAccount,
    ({ isDefaultPayout }) => ({
      bankName: verifiedData.bankName,
      accountName: verifiedData.accountName,
      accountNumber: verifiedData.accountNumber,
      swiftCode: verifiedData.swiftCode,
      branchCode: verifiedData.branchCode,
      isDefaultPayout,
      // FUTURE RELEASE: restore ternary when MSISDN is re-enabled:
      // type: step1Draft.verificationMethod === "msisdn" ? "MSISDN" : "BANK",
      type: "BANK",
    }),
    () => {
      resetFlow();
      closeModal("bank");
    },
    "bank",
  );

  return {
    modalOpened: modalState.bank,
    currentStep,
    step1Draft,
    verifiedData,
    isVerifying,
    isSaving,
    openModal: handleOpenModal,
    closeModal: handleCloseModal,
    goToStep2: handleGoToStep2,
    goBackToStep1: handleGoBackToStep1,
    handleSave,
    setStep1Draft,
    verifyBankAccount,
    // FUTURE RELEASE: re-expose verifyViaMsisdn when MSISDN bank verification is supported
    // verifyViaMsisdn,
  };
};

export default useBankAccountHandlers;
